import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Plus, Edit, Trash2, Eye, Calendar } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1585829365295-ab7cd400c167?w=400&h=400&fit=crop';

const ArticleManagement = () => {
  const { getAuthHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [uploading, setUploading] = useState(false);

  const getImageUrl = (url) => {
    if (!url) return FALLBACK_IMAGE;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/uploads') || url.startsWith('/uploads')) return `${API_URL}${url}`;
    return url;
  };

  const [formData, setFormData] = useState({
    title_bengali: '',
    title_english: '',
    location: 'Agartala',
    category: 'সাধারণ',
    category_english: 'General',
    content_bengali: '',
    content_english: '',
    image_url: '',
    is_breaking: false,
    is_featured: false,
  });

  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchArticles();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/articles`, getAuthHeaders());
      setArticles(response.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch articles', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    uploadFormData.append('category', 'articles');

    try {
      const response = await axios.post(
        `${API_URL}/api/admin/upload/image`,
        uploadFormData,
        {
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      setFormData({ ...formData, image_url: response.data.url });
      toast({ title: 'Success', description: 'Image uploaded successfully' });
    } catch (error) {
      toast({ title: 'Error', description: 'Image upload failed', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title_bengali: '',
      title_english: '',
      location: 'Agartala',
      content_bengali: '',
      content_english: '',
      image_url: '',
      is_breaking: false,
      is_featured: false,
    });
    setEditingArticle(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingArticle) {
        await axios.put(
          `${API_URL}/api/admin/articles/${editingArticle.id}`,
          formData,
          getAuthHeaders()
        );
        toast({ title: 'Success', description: 'Article updated successfully' });
      } else {
        await axios.post(`${API_URL}/api/admin/articles`, formData, getAuthHeaders());
        toast({ title: 'Success', description: 'Article created successfully' });
      }
      
      resetForm();
      fetchArticles();
      setShowModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save article', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;

    try {
      await axios.delete(`${API_URL}/api/admin/articles/${id}`, getAuthHeaders());
      toast({ title: 'Success', description: 'Article deleted successfully' });
      fetchArticles();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete article', variant: 'destructive' });
    }
  };

  const handleEdit = (article) => {
    setEditingArticle(article);
    setFormData({
      title_bengali: article.title_bengali || '',
      title_english: article.title_english || '',
      location: article.location || 'Agartala',
      content_bengali: article.content_bengali || '',
      content_english: article.content_english || '',
      image_url: article.image_url || '',
      is_breaking: article.is_breaking || false,
      is_featured: article.is_featured || false,
    });
    setShowModal(true);
  };

  // Categories removed as per user requirement - text-only articles without categories

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Article Management</h1>
          <p className="text-gray-600 mt-1">Manage news articles and publications</p>
        </div>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-red-600 hover:bg-red-700">
          <Plus className="w-4 h-4 mr-2" />
          Add New Article
        </Button>
      </div>

      {/* Articles List */}
      <div className="grid gap-6">
        {loading && articles.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          </div>
        ) : articles.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No articles yet. Create your first article!</p>
            </CardContent>
          </Card>
        ) : (
          articles.map((article) => (
            <Card key={article.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex gap-4">
                  <img
                    src={getImageUrl(article.image_url)}
                    alt={article.title_english}
                    className="w-32 h-32 object-cover rounded-lg"
                    onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="text-xl font-bold mb-1">{article.title_bengali}</h3>
                        <p className="text-gray-600">{article.title_english}</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(article)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(article.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                      <span className="text-sm text-gray-500">{article.location}</span>
                      {article.category && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {article.category}
                        </Badge>
                      )}
                      {article.is_breaking && <Badge variant="destructive">Breaking</Badge>}
                      {article.is_featured && <Badge className="bg-yellow-500">Featured</Badge>}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {article.date}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="w-4 h-4" />
                        {article.views} views
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? 'Edit Article' : 'Add New Article'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Title (Bengali) * <span className="text-red-500 text-xs">(Required)</span></Label>
                <Input
                  value={formData.title_bengali}
                  onChange={(e) => setFormData({ ...formData, title_bengali: e.target.value })}
                  placeholder="বাংলায় শিরোনাম লিখুন... (Enter Bengali title here)"
                  required
                  data-testid="title-bengali-input"
                />
              </div>
              <div>
                <Label>Title (English) <span className="text-gray-500 text-xs">(Optional)</span></Label>
                <Input
                  value={formData.title_english}
                  onChange={(e) => setFormData({ ...formData, title_english: e.target.value })}
                  placeholder="Enter English title here (optional)"
                  data-testid="title-english-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Location *</Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="স্থান লিখুন (e.g., Agartala, Udaipur, Dharmanagar)"
                  required
                  data-testid="location-input"
                />
              </div>
              <div>
                <Label>Category (বিভাগ) *</Label>
                <select
                  value={formData.category}
                  onChange={(e) => {
                    const selectedCategory = categories.find(cat => cat.bengali === e.target.value);
                    setFormData({
                      ...formData,
                      category: e.target.value,
                      category_english: selectedCategory?.english || 'General'
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  {categories.map((cat) => (
                    <option key={cat.bengali} value={cat.bengali}>
                      {cat.bengali} ({cat.english})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label>Article Image <span className="text-gray-500 text-xs">(Optional)</span></Label>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  disabled={uploading}
                  className="cursor-pointer"
                />
                {uploading && (
                  <p className="text-sm text-gray-500">Uploading image...</p>
                )}
                {formData.image_url && (
                  <div className="mt-2">
                    <img
                      src={getImageUrl(formData.image_url)}
                      alt="Article preview"
                      className="w-32 h-32 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({ ...formData, image_url: '' })}
                      className="mt-2"
                    >
                      Remove Image
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label>Content (Bengali) * <span className="text-red-500 text-xs">(Required)</span></Label>
              <Textarea
                value={formData.content_bengali}
                onChange={(e) => setFormData({ ...formData, content_bengali: e.target.value })}
                placeholder="বাংলায় সম্পূর্ণ খবর লিখুন... (Enter full news article in Bengali)"
                rows={4}
                required
                data-testid="content-bengali-input"
              />
            </div>

            <div>
              <Label>Content (English) <span className="text-gray-500 text-xs">(Optional)</span></Label>
              <Textarea
                value={formData.content_english}
                onChange={(e) => setFormData({ ...formData, content_english: e.target.value })}
                placeholder="Enter full news article in English (optional)"
                rows={4}
                data-testid="content-english-input"
              />
            </div>

            <div className="flex gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_breaking}
                  onChange={(e) => setFormData({ ...formData, is_breaking: e.target.checked })}
                />
                <span>Breaking News</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                />
                <span>Featured Article</span>
              </label>
            </div>

            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={loading}>
                {loading ? 'Saving...' : editingArticle ? 'Update' : 'Publish'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArticleManagement;